// Comms next pass (1.12.214-beta): the reply box's Templates → "Edit templates" goes to Settings → Templates on the
// matching channel tab, and brings the person back to the same conversation with their unsent reply still there.
//
// The unsent draft rides in sessionStorage (this tab only, cleared on restore, never sent anywhere) because leaving
// /communications unmounts the in-memory draft store. Plain JS: no React, so it is easy to test.

export const TEMPLATE_RETURN_STORAGE_KEY = 'ia:comms-template-settings-return';
// A return note older than this is ignored (the person clearly moved on).
export const TEMPLATE_RETURN_MAX_AGE_MS = 6 * 60 * 60 * 1000;
export const TEMPLATE_SETTINGS_CHANNELS = ['sms', 'whatsapp', 'email'];

/** Settings → Templates, on the tab for this channel (mms and unknown → SMS), optionally opening one template. */
export function templateSettingsHref(channel, templateId = '') {
    const key = String(channel || '').toLowerCase();
    const tab = TEMPLATE_SETTINGS_CHANNELS.includes(key) ? key : 'sms';
    const base = `/admin/email-channel?tab=templates&channel=${tab}`;
    return templateId ? `${base}&template=${encodeURIComponent(templateId)}` : base;
}

function storage() {
    try {
        return typeof window !== 'undefined' && window.sessionStorage ? window.sessionStorage : null;
    } catch {
        return null; // storage blocked (private mode, sandboxed frame): the trip still works, only the draft isn't kept
    }
}

/**
 * Remember where to come back to. kind 'reply' = an open conversation (sessionId); kind 'compose' = the New message form.
 * @returns {boolean} true when it was stored.
 */
export function saveTemplateSettingsReturn({ kind = 'reply', sessionId = '', body = '', subject = '', channel = '' } = {}, now = Date.now()) {
    const store = storage();
    if (!store) return false;
    if (kind === 'reply' && !sessionId) return false;
    const note = {
        kind: kind === 'compose' ? 'compose' : 'reply',
        sessionId: sessionId ? String(sessionId) : '',
        body: typeof body === 'string' ? body : '',
        subject: typeof subject === 'string' ? subject : '',
        channel: typeof channel === 'string' ? channel : '',
        savedAt: now,
    };
    try {
        store.setItem(TEMPLATE_RETURN_STORAGE_KEY, JSON.stringify(note));
        return true;
    } catch {
        return false;
    }
}

/** The stored return note, or null when missing, unreadable or stale. Does not clear it. */
export function readTemplateSettingsReturn(now = Date.now()) {
    const store = storage();
    if (!store) return null;
    try {
        const parsed = JSON.parse(store.getItem(TEMPLATE_RETURN_STORAGE_KEY) || 'null');
        if (!parsed || typeof parsed !== 'object') return null;
        if (!Number.isFinite(parsed.savedAt) || now - parsed.savedAt > TEMPLATE_RETURN_MAX_AGE_MS || parsed.savedAt > now + 60000) return null;
        if (parsed.kind === 'reply' && !parsed.sessionId) return null;
        return {
            kind: parsed.kind === 'compose' ? 'compose' : 'reply',
            sessionId: String(parsed.sessionId || ''),
            body: typeof parsed.body === 'string' ? parsed.body : '',
            subject: typeof parsed.subject === 'string' ? parsed.subject : '',
            channel: typeof parsed.channel === 'string' ? parsed.channel : '',
            savedAt: parsed.savedAt,
        };
    } catch {
        return null;
    }
}

export function clearTemplateSettingsReturn() {
    const store = storage();
    try { store?.removeItem(TEMPLATE_RETURN_STORAGE_KEY); } catch { /* nothing to clear */ }
}

/** Where "Back to conversation" goes. */
export function templateSettingsReturnHref(note) {
    if (note?.kind === 'reply' && note.sessionId) return `/communications?session=${encodeURIComponent(note.sessionId)}`;
    return '/communications'; // the New message form reopens from the stored note
}

// Comms final pass (#759 follow-up): the side panel's "Edit wording in Template Builder" and "Manage templates" links used to be
// plain <a href> page loads, which threw the unsent draft away. They now ask the open composer to go (it saves the draft and
// navigates in-app, exactly like Templates → "Edit templates"); with no composer listening the caller navigates in-app itself.
export const OPEN_TEMPLATE_SETTINGS_EVENT = 'ia:open-template-settings';

/** @returns {boolean} true when a composer took the request (and saved its draft). */
export function requestOpenTemplateSettings(channel = '', templateId = '') {
    if (typeof window === 'undefined') return false;
    const detail = { channel: String(channel || ''), templateId: String(templateId || ''), handled: false };
    window.dispatchEvent(new CustomEvent(OPEN_TEMPLATE_SETTINGS_EVENT, { detail }));
    return detail.handled === true;
}

/** A composer listens while it is on screen; the first listener takes the request. Returns the unsubscribe function. */
export function onOpenTemplateSettingsRequest(handler) {
    if (typeof window === 'undefined' || typeof handler !== 'function') return () => {};
    const listener = (event) => {
        const detail = event?.detail;
        if (!detail || detail.handled) return;
        detail.handled = true;
        handler(detail);
    };
    window.addEventListener(OPEN_TEMPLATE_SETTINGS_EVENT, listener);
    return () => window.removeEventListener(OPEN_TEMPLATE_SETTINGS_EVENT, listener);
}
